import { useEffect, useRef, useState } from "react";
import { useBarragemContext } from "../../context";
import { Paginated, RequestError, useApi } from "../../services";
import { ConfirmModal, ErrorOnDialog, LoadingModal } from "../modals";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import { generateRandomHash, useWindowSize } from "../../core";
import { StyleButton } from "../mapa";
import { Municipio, SelectMunicipio } from "../estadoMunicipio";
import { MDXEditorMethods } from "@mdxeditor/editor";
import { HidroDXEditor } from "../edit";
import { Acomodacao, AcomodacaoData } from "../zas";

export interface TableAcomodacoesProps {
  notPaginated?: boolean;
  hideTitle?: boolean;
  editable?: boolean;
  onSelect?: (acomodacao: Acomodacao) => void;
}

export const TableAcomodacoes: React.FC<TableAcomodacoesProps> = ({ hideTitle, editable, notPaginated, onSelect }) => {
  const api = useApi();
  const { barragem } = useBarragemContext();
  const { height, width } = useWindowSize();
  // const { palette } = useTheme();

  const divRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  const [size, setSize] = useState({ width: 0, height: 0 });
  const [openNew, setOpenNew] = useState(false);
  const [acomodacaoEdit, setAcomodacaoEdit] = useState<AcomodacaoData>();
  const [acomodacaoDelete, setAcomodacaoDelete] = useState<AcomodacaoData>();
  const [error, setError] = useState<RequestError>();
  const [state, setState] = useState(false);
  const [acomodacoes, setAcomodacoes] = useState<Paginated<Acomodacao>>({
    items: [],
    page: 1,
    perPage: 10,
    totalItems: 0,
    totalPages: 1,
  });

  useEffect(() => {
    if (width < 600) {
      setAcomodacoes((prevState) => ({
        ...prevState,
        perPage: 8,  // Alterando para 8 quando a largura da tela for pequena
      }));
    } else {
      setAcomodacoes((prevState) => ({
        ...prevState,
        perPage: 10,  // Caso contrário, mantém 10
      }));
    }
  }, [width]);

  useEffect(() => {
    if (!barragem || "error" in barragem) return;

    api.acomodacoes.getAll(barragem.id, acomodacoes.page, acomodacoes.perPage).then((value) => {
      if ("error" in value) {
        setError(value);
        return;
      }
      setAcomodacoes(value);
    });

  }, [state, barragem, acomodacoes.page, acomodacoes.perPage]);

  // Tamanho da tabela
  useEffect(() => {
    if (!divRef.current) return;
    const { width } = divRef.current.getBoundingClientRect();
    let { height } = divRef.current.getBoundingClientRect();

    if (titleRef.current) {
      const size = titleRef.current.getBoundingClientRect();
      height -= size.height;
    }
    if (paginationRef.current) {
      const size = paginationRef.current.getBoundingClientRect();
      height -= size.height;
    }

    setSize({ width, height });
  }, [divRef, titleRef, paginationRef, height, width]);

  // Obtendo os dados
  useEffect(() => {
    if (!barragem || "error" in barragem) return;


    api.acomodacoes.getAll(barragem.id, acomodacoes.page, acomodacoes.perPage).then(async (value) => {
      if ("error" in value) {
        setError(value);
        return;
      }

      if (!notPaginated) {
        setAcomodacoes(value);
        return;
      }

      // Obtendo todos os itens
      const items: Acomodacao[] = value.items;
      for (let page = 1; page < value.totalPages; page++) {
        const value = await api.acomodacoes.getAll(barragem.id, page, acomodacoes.perPage);

        if ("error" in value) {
          setError(value);
          return;
        }

        for (const item of value.items) {
          items.push(item);
        }
      }

      setAcomodacoes(value);
    });


  }, [barragem]);

  const handleEdit = (value: AcomodacaoData) => {
    const acomodacao = new Acomodacao(value, value.style);
    api.acomodacoes.update(acomodacao).then((val) => {
      if ("error" in val) {
        setError(val);
        return;
      }

      setOpenNew(false);
      setAcomodacaoEdit(undefined);
      setState(!state);
    });
  };

  const handleDelete = (value: AcomodacaoData) => {
    setAcomodacaoDelete(undefined);
    api.acomodacoes.remove(value.id).then((val) => {
      if ("error" in val) {
        setError(val);
        return;
      }
      setState(!state);
    });
  };

  const handleAdd = (value: AcomodacaoData) => {
    if (!barragem || "error" in barragem) return;

    const acomodacao = new Acomodacao(value, value.style);
    api.acomodacoes.add(barragem.id, acomodacao).then((val) => {
      if ("error" in val) {
        setError(val);
        return;
      }

      setOpenNew(false);
      setAcomodacaoEdit(undefined);
      setState(!state);
    });
  };


  return <>
    {/* Modal Error */}
    <ErrorOnDialog
      open={Boolean(error)}
      error={error?.error ?? ""}
      message={error?.message ?? ""}
      onClose={() => setError(undefined)}
    />

    {/* Delete Modal */}
    <ConfirmModal
      open={Boolean(acomodacaoDelete)}
      title="Deseja mesmo excluir esta acomodação?"
      message="A ação de excluir uma acomodação é irreversível!"
      onCancel={() => setAcomodacaoDelete(undefined)}
      onConfirm={() => acomodacaoDelete && handleDelete(acomodacaoDelete)}
    />

    {/* Modal add or edit */}
    {openNew && <ModalEdit
      open={openNew}
      item={acomodacaoEdit}
      onClose={() => {
        setOpenNew(false);
        setAcomodacaoEdit(undefined);
      }}
      onFinish={(newAcomodacao) => {
        if ("error" in newAcomodacao) {
          setError(newAcomodacao);
        }
        else if (acomodacaoEdit) {
          handleEdit(newAcomodacao);
        } else {
          handleAdd(newAcomodacao);
        }
      }}
    />}

    {/* Table */}
    <Box
      ref={divRef}
      sx={{
        gap: "0.5em",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "auto"
      }}>

      {/* Título e botão de adicionar*/}
      {(!hideTitle || editable) && <Box ref={titleRef} sx={{ display: "flex", flexDirection: "row" }}>
        {!hideTitle && <Typography variant="h4">Acomodações das Pessoas Evacuadas</Typography>}

        <div style={{ flex: 1 }} />

        {editable && <Tooltip title="Adicionar nova acomodação">
          <IconButton onClick={() => setOpenNew(true)}>
            <Add />
          </IconButton>
        </Tooltip>}
      </Box>}


      {/* Tabela */}
      <TableContainer component={Paper} sx={{ flex: 1 }}>
        <Table size='small' sx={{ flex: 1, width: "100%", height: size.height, overflow: "auto" }}>
          <TableHead>
            <TableRow>
              <TableCell align="center">Nome</TableCell>
              <TableCell align="center">&nbsp;Latitude&nbsp;&nbsp;</TableCell>
              <TableCell align="center">&nbsp;Longitude&nbsp;</TableCell>
              <TableCell align="center">&nbsp;&nbsp;&nbsp;Tipo&nbsp;&nbsp;&nbsp;</TableCell>
              <TableCell align="center">Instalações</TableCell>
              <TableCell align="center">Marcador</TableCell>
              {editable && <TableCell align="center">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Ações&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {acomodacoes.items.map((acomodacao, idx) => (
              <TableRow
                key={idx}
                hover
                onClick={() => onSelect?.(acomodacao)}
              >
                <TableCell>{acomodacao.properties.nome}</TableCell>
                <TableCell>{acomodacao.properties.lat.toSignificantDigits("latStr")}</TableCell>
                <TableCell>{acomodacao.properties.lng.toSignificantDigits("lngStr")}</TableCell>
                <TableCell>{acomodacao.properties.tipo}</TableCell>
                <TableCell align="center"><Instalacao key={generateRandomHash(8)} text={acomodacao.properties.instalacoes ?? ""} /></TableCell>

                <TableCell>
                  <StyleButton
                    initialStyle={acomodacao.style ?? { iconStr: "Hotel", iconSize: 25, color: "#583506", }}
                    initialType="Marker"
                    onChange={(style) => {
                      if (!editable) return;
                      acomodacao.style = style;
                      handleEdit(acomodacao.properties);
                    }}
                  />
                </TableCell>
                {/* Actions */}
                {editable && (
                  <TableCell>
                    <IconButton onClick={() => {
                      setAcomodacaoEdit(acomodacao.properties);
                      setOpenNew(true);
                    }}>
                      <Edit />
                    </IconButton>

                    <IconButton onClick={() => setAcomodacaoDelete(acomodacao.properties)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!notPaginated && <TablePagination
        component="div"
        ref={paginationRef}
        count={acomodacoes.totalItems}
        page={acomodacoes.page - 1}
        onPageChange={(_, newPage) => setAcomodacoes({ ...acomodacoes, page: newPage + 1 })}
        rowsPerPage={acomodacoes.perPage}
        onRowsPerPageChange={(e) => setAcomodacoes({ ...acomodacoes, perPage: parseInt(e.target.value, 10), page: 1 })}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />}

    </Box >
  </>;
};


interface ModalProps {
  open: boolean;
  onClose: () => void;
  item?: AcomodacaoData;
  onFinish: (acomodacao: AcomodacaoData | RequestError) => void;
}

const ModalEdit: React.FC<ModalProps> = ({ open, onClose, item, onFinish }) => {
  const api = useApi();

  const editorRef = useRef<MDXEditorMethods>(null);
  const startText = useRef<string>();

  const [loading, setLoading] = useState(false);
  const [municipio, setMunicipio] = useState<Municipio>();
  const [acomodacao, setAcomodacao] = useState<AcomodacaoData>(item ?? {
    id: -1,
    idBarragem: 0,
    nome: "",
    lat: 0,
    lng: 0,
    tipo: "",
    endereco: "",
    municipio: 0,
    idUpdate: "",
    dataCadastro: new Date(),
    style: { iconStr: "Hotel", iconSize: 25, color: "#583506" },
  });


  useEffect(() => {
    setTimeout(() => {
      startText.current = acomodacao.instalacoes;
      editorRef.current?.setMarkdown(acomodacao.instalacoes ?? "");
    }, 1000);
  }, []);

  useEffect(() => {
    setLoading(true);
    api.estadosMunicipios.getMunicipio(acomodacao.municipio).then((value) => {
      setLoading(false);
      if (!("error" in value)) setMunicipio(value);
    });
  }, []);

  return <>
    <LoadingModal open={loading} />

    <Dialog open={open} onClose={() => {
      setMunicipio(undefined);
      setAcomodacao({
        id: -1,
        idBarragem: 0,
        nome: "",
        lat: 0,
        lng: 0,
        tipo: "",
        endereco: "",
        municipio: 0,
        idUpdate: "",
        dataCadastro: new Date(),
        style: { iconStr: "Hotel", iconSize: 25, color: "#583506" },
      });
      onClose();
    }}>
      <DialogTitle>
        {item ? "Editar Acomodação" : "Adicionar Nova Acomodação"}
      </DialogTitle>

      <DialogContent>
        <Grid2 container spacing="0.5em">
          {/* Estilo */}
          <Grid2 size={1}>
            <Tooltip title="Estilo">
              <StyleButton
                initialStyle={acomodacao.style ?? { iconStr: "Hotel", iconSize: 25, color: "#583506", }}
                onChange={(style) => setAcomodacao({ ...acomodacao, style: style })}
                initialType="Marker"
              />
            </Tooltip>
          </Grid2>

          {/* Nome */}
          <Grid2 size={7}>
            <Box sx={{
              padding: "0",
              display: "flex",
              alignIntems: "center",
              flexDirection: "column",
              justifyContent: "center",
            }}>
              <TextField fullWidth label="Nome" variant="standard"
                value={acomodacao.nome}
                onChange={(ev) => setAcomodacao({
                  ...acomodacao,
                  nome: ev.target.value,
                })}
                slotProps={{ htmlInput: { maxLength: 100 } }}
              />
              <Typography
                variant="caption"
                color={acomodacao.nome.length > 80 ? "error" : (acomodacao.nome.length > 50 ? "warning" : undefined)}
              >
                {acomodacao.nome.length}/100 caracteres
              </Typography>
            </Box>
          </Grid2>

          {/* Município */}
          <Grid2 size={4}>
            <SelectMunicipio
              key={generateRandomHash(5)}
              variant="outlined"
              municipio={municipio}
              onChoose={(muni) => setMunicipio(muni)}
            />
          </Grid2>

          {/* Lat */}
          <Grid2 size={4}>
            <TextField fullWidth label="Latitude" variant="standard"
              value={acomodacao.lat.toString().replaceAll(".", ",")}
              onChange={(ev) => {
                const value = parseFloat(ev.target.value.replaceAll(",", "."));
                if (isNaN(value)) return;

                setAcomodacao({
                  ...acomodacao,
                  lat: value,
                });
              }}
            />
          </Grid2>

          {/* Lng */}
          <Grid2 size={4}>
            <TextField fullWidth label="Longitude" variant="standard"
              value={acomodacao.lng.toString().replaceAll(".", ",")}
              onChange={(ev) => {
                const value = parseFloat(ev.target.value.replaceAll(",", "."));
                if (isNaN(value)) return;

                setAcomodacao({
                  ...acomodacao,
                  lng: value,
                });
              }}
            />
          </Grid2>

          {/* Altitude */}
          <Grid2 size={4}>
            <TextField fullWidth label="Altitude" variant="standard"
              value={acomodacao.alt?.toString().replaceAll(".", ",")}
              onChange={(ev) => {
                if (ev.target.value === "") {
                  setAcomodacao({ ...acomodacao, alt: undefined });
                  return;
                }

                const value = parseFloat(ev.target.value.replaceAll(",", "."));
                if (isNaN(value)) return;
                setAcomodacao({ ...acomodacao, alt: value });
              }}
            />
          </Grid2>

          {/* Tipo */}
          <Grid2 size={4}>
            <Box sx={{
              padding: "0",
              display: "flex",
              alignIntems: "center",
              flexDirection: "column",
              justifyContent: "center",
            }}>
              <TextField fullWidth label="Tipo" variant="standard"
                value={acomodacao.tipo}
                onChange={(ev) => setAcomodacao({ ...acomodacao, tipo: ev.target.value })}
                slotProps={{ htmlInput: { maxLength: 50 } }}
              />
              <Typography
                variant="caption"
                color={acomodacao.tipo.length > 30 ? "error" : (acomodacao.tipo.length > 45 ? "warning" : undefined)}
              >
                {acomodacao.tipo.length}/50 caracteres
              </Typography>
            </Box>
          </Grid2>

          {/* Endereço */}
          <Grid2 size={8}>
            <TextField fullWidth label="Endereço" variant="standard"
              value={acomodacao.endereco}
              onChange={(ev) => setAcomodacao({ ...acomodacao, endereco: ev.target.value })}
            />
          </Grid2>

          {/* Instalações */}
          <Grid2 size={12}>
            <Paper sx={{ p: "0.5em", width: "100%", height: "15em", overflow: "auto" }}>
              <Typography>Instalações:</Typography>
              <HidroDXEditor
                image={false}
                chartMap={false}
                readOnly={false}
                editorRef={editorRef}
                onChange={(text) => setAcomodacao({ ...acomodacao, instalacoes: text })}
                diffText={startText.current}
              />
            </Paper>
          </Grid2>
        </Grid2>

      </DialogContent>

      <DialogActions>
        <Button onClick={() => {
          setMunicipio(undefined);
          setAcomodacao({
            id: -1,
            idBarragem: 0,
            nome: "",
            lat: 0,
            lng: 0,
            tipo: "",
            endereco: "",
            municipio: 0,
            idUpdate: "",
            dataCadastro: new Date(),
          });
          onClose();
        }} variant="contained">
          Cancelar
        </Button>

        <Button onClick={() => {
          if (!municipio || Array.isArray(municipio) && municipio.length === 0) {
            onFinish({ error: "Erro", message: "Você não definiu o município da acomodação" });
          }
          else if (!acomodacao.nome || !acomodacao.lat || !acomodacao.lng || !acomodacao.tipo || !acomodacao.endereco) {
            onFinish({ error: "Erro", message: "Você não preencheu todos os campos obrigatórios" });
          }
          else {
            acomodacao.municipio = municipio.id;
            onFinish(acomodacao);
            setMunicipio(undefined);
            setAcomodacao({
              id: -1,
              idBarragem: 0,
              nome: "",
              lat: 0,
              lng: 0,
              tipo: "",
              endereco: "",
              municipio: 0,
              idUpdate: "",
              dataCadastro: new Date(),
            });
          }
        }}>
          {item ? "Editar" : "Adicionar"}
        </Button>
      </DialogActions>

    </Dialog>
  </>;
};

const Instalacao: React.FC<{ text: string; }> = ({ text }) => {
  const editorRef = useRef<MDXEditorMethods>(null);

  useEffect(() => {
    editorRef.current?.setMarkdown(text);
  }, [editorRef.current]);

  return <Box sx={{ p: "0.5em", width: "100%", textAlign: "center" }}>
    <HidroDXEditor
      image={false}
      chartMap={false}
      readOnly={true}
      editorRef={editorRef}
    />
  </Box>;
};
